import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  Asset,
  BaseData,
  BaseDataEntity,
  DividendsData,
  FactsEntity,
  InstrumentBaseDataModel,
  InstrumentType,
  KeyData,
  ProfileData,
} from '@zero/shared/util-models';
import { environment } from 'environments/environment';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class InstrumentBaseDataService {
  private baseDataInstrumentType: 'shares' | 'etfs' | 'derivatives' | 'funds' = undefined;

  constructor(private _http: HttpClient) {}

  public loadBaseData = async (asset: Asset): Promise<InstrumentBaseDataModel> => {
    const { isin, instrumentType, shortName } = asset;
    if (instrumentType === InstrumentType.Crypto) {
      return {
        baseData: {
          shortName,
          instrumentType,
        },
      };
    }

    this.setBaseDataInstrumentType(instrumentType);
    if (!this.baseDataInstrumentType) {
      return undefined;
    }

    let url = '';
    switch (this.baseDataInstrumentType) {
      case 'derivatives':
        url = `${environment.apihost}/extapis-c1h/findata/instrument/derivatives/${isin}`;
        break;
      case 'shares':
        url = `${environment.apihost}/extapis-c1d/findata/instrument/shares/${isin}`;
        break;
      default:
        url = `${environment.apihost}/extapis-c1w/findata/instrument/${this.baseDataInstrumentType}/${isin}`;
        break;
    }

    const response = await firstValueFrom(this._http.get<BaseDataEntity>(url));
    if (!response?.Facts?.length) {
      return undefined;
    }

    const { Facts } = response;
    const { BASE_DATA, KEY_DATA, COMPANY_PROFILE, CONDITIONS, PROFILE, DIVIDENDS } = BaseDataType;
    return {
      baseData: {
        ...this.getBaseData(
          Facts.find(({ headline }) => headline === BASE_DATA),
          Facts.find(({ headline }) => headline === CONDITIONS),
        ),
        instrumentType,
      },
      keyData: this.getKeyData(Facts.find(({ headline }) => headline === KEY_DATA)),
      profile:
        this.getProfile(
          Facts.find(({ headline }) => headline === COMPANY_PROFILE),
          COMPANY_PROFILE,
        ) ??
        this.getProfile(
          Facts.find(({ headline }) => headline === PROFILE),
          PROFILE,
        ),
      dividends: this.getDividends(
        Facts.find(({ headline }) => headline === DIVIDENDS),
        DIVIDENDS,
      ),
    };
  };

  private setBaseDataInstrumentType = (instrumentType: InstrumentType): void => {
    switch (instrumentType) {
      case InstrumentType.Share:
        this.baseDataInstrumentType = 'shares';
        break;
      case InstrumentType.Etf:
        this.baseDataInstrumentType = 'etfs';
        break;
      case InstrumentType.Derivative:
        this.baseDataInstrumentType = 'derivatives';
        break;
      case InstrumentType.Funds:
        this.baseDataInstrumentType = 'funds';
        break;
      default:
        return;
    }
  };

  private getBaseData = (baseData?: FactsEntity, conditions?: FactsEntity): BaseData => {
    if (!baseData?.values?.length) {
      return undefined;
    }

    const { values } = baseData;

    const base = {
      wkn: values.find(({ key }) => key === 'WKN')?.value,
      isin: values.find(({ key }) => key === 'ISIN')?.value,
    };

    switch (this.baseDataInstrumentType) {
      case 'shares':
        return {
          ...base,
          branches: values.find(({ key }) => key === 'Branchen')?.value,
          country: values.find(({ key }) => key === 'Land')?.value,
        };
      case 'etfs':
      case 'funds':
        return {
          ...base,
          emittent: values.find(({ key }) => key === 'Emittent')?.value,
          ter: conditions?.values.find(({ key }) => key === 'TER')?.percent,
          setupCountry: values.find(({ key }) => key === 'Aufgelegt in')?.value,
          releaseDate: values.find(({ key }) => key === 'Auflagedatum')?.date,
          category: values.find(({ key }) => key === 'Fondskategorie')?.value,
          volume: {
            value: values.find(({ key }) => key === 'Volumen')?.current,
            symbol: values.find(({ key }) => key === 'Volumen')?.symbol,
          },
          manager: values.find(({ key }) => key === 'Fondsmanager')?.value,
          replication: values.find(({ key }) => key === 'Replikation')?.value,
          isAccumulating: conditions?.values?.find(({ key }) => key === 'Ausschüttend')?.boolean,
        };
      case 'derivatives':
        return {
          ...base,
          emittent: values.find(({ key }) => key === 'Emittent')?.value,
          productType: values.find(({ key }) => key === 'Produkttyp')?.value,
          investmentDirection: values.find(({ key }) => key === 'Investmentrichtung')?.value,
          ratio: values.find(({ key }) => key === 'Ratio')?.current,
          typeOfSettlement: values.find(({ key }) => key === 'Art der Abwicklung')?.value,
          typeOfExercise: values.find(({ key }) => key === 'Art der Ausübung')?.value,
        };
      default:
        return undefined;
    }
  };

  private getKeyData = (keyData: FactsEntity): KeyData => {
    if (this.baseDataInstrumentType !== 'shares' || !keyData?.values?.length) {
      return undefined;
    }

    const { values } = keyData;
    const marketCap = values.find(({ key }) => key === 'Marktkapitalisierung');
    const earningsPerShare = values.find(({ key }) => key === 'Gewinn je Aktie');
    const emissionPrice = values.find(({ key }) => key === 'Emissionspreis');
    return {
      marketCap: marketCap?.current,
      marketCapSymbol: marketCap?.symbol,
      earningsPerShare: earningsPerShare?.current,
      earningsPerShareSymbol: earningsPerShare?.symbol,
      emissionPrice: emissionPrice?.current,
      emissionPriceSymbol: emissionPrice?.symbol,
      numberOfShares: values.find(({ key }) => key === 'Anzahl Aktien')?.current,
      kbv: values.find(({ key }) => key === 'KBV')?.current,
      kcv: values.find(({ key }) => key === 'KCV')?.current,
      kgv: values.find(({ key }) => key === 'KGV aktuell')?.current,
      vola: values.find(({ key }) => key === 'Vola 250 Tage')?.current,
      freeFloatPct: values.find(({ key }) => key === 'Streubesitz in %')?.current,
    };
  };

  private getProfile = (profile: FactsEntity, headline: BaseDataType): ProfileData => {
    if (!profile?.values?.length) {
      return undefined;
    }

    return {
      headline,
      text: profile.values[0].key,
    };
  };

  private getDividends = (dividends: FactsEntity, headline: BaseDataType): DividendsData => {
    return dividends?.values?.length ? new DividendsData(headline, dividends.values) : undefined;
  };
}

enum BaseDataType {
  BASE_DATA = 'Stammdaten',
  KEY_DATA = 'Kennzahlen',
  COMPANY_PROFILE = 'Unternehmensprofil',
  PROFILE = 'Profil',
  CONDITIONS = 'Konditionen',
  DIVIDENDS = 'Dividendenkalender',
}
